import React from 'react';
import { get } from 'lodash';
import { pipe, map, path, flatten, compact, uniqBy } from 'lodash/fp';
import { tAlt } from 'Components/utils/booking/bookingUtils';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import {
  toggleAdditionalAccommodationFilter,
  toggleAdditionalAccommodationReducedMobilityFilter,
  togglePMRChecbox,
} from 'Store/booking/additional-accommodations/additionalAccommodations.action';
import { CABIN_TYPE, ROOM_TYPE } from 'Utils/accommodation/accommodation.constants';
import { FilterMobility } from 'Components/widgets/filter-mobility/FilterMobility';
type Category = {
  id: string;
};
type Strength = {
  id: string;
  label: string;
};
const COMFORT_CATEGORY = 'accommodations';
const EXTRA_FEATURES_CATEGORY = 'extra';
const CONFIGURATION_CATEGORY = 'configuration';
const REDUCED_MOBILITY_CATEGORY = 'reduced_mobility';
const REDUCED_MOBILITY_FILTER_ID = 'reduced_mobility_access';

const getOptions = (v, c) =>
  v
    .filter(({ category }) => category === c)
    .map((o) => ({
      iconName: o.meta?.icon,
      id: o.id,
      isDisabled: !o.matchedOccurrences,
      isSelected: o.selected,
      label: o.meta?.label,
      matched: o.matchedOccurrences || 0,
      value: o.value,
    }));

export const getFilters = (
  product: Product,
  filterValues: Array<Filter> = [],
  options?: {
    ignoreReducedMobility: boolean;
  },
): Array<FilterItem> =>
  [
    {
      id: COMFORT_CATEGORY,
      label: t('filters.comfort.categoryTitle'),
      options: getOptions(filterValues, COMFORT_CATEGORY),
    },
    {
      id: CONFIGURATION_CATEGORY,
      label: tAlt({
        cruiseKeyPath: 'filters.configuration.categoryTitleCruise',
        product,
        resortKeyPath: 'filters.configuration.categoryTitle',
      }),
      options: getOptions(filterValues, CONFIGURATION_CATEGORY),
    },
    {
      id: EXTRA_FEATURES_CATEGORY,
      label: t('filters.extraFeatures.categoryTitle'),
      options: getOptions(filterValues, EXTRA_FEATURES_CATEGORY),
    },
    options?.ignoreReducedMobility
      ? null
      : {
          id: REDUCED_MOBILITY_CATEGORY,
          label: '',
          options: getOptions(filterValues, EXTRA_FEATURES_CATEGORY),
          // eslint-disable-next-line clubmed/no-fake-component
          customRenderParent: (selectedOptions, onChange, isDisabled) => (
            <FilterMobility
              isDisabled={isDisabled}
              onChange={onChange}
              selectedOptions={selectedOptions}
            />
          ),
        },
  ].filter(Boolean);

const matchConfiguration = (accommodations: Array<Accommodation>, roomType: string): boolean => {
  const [SINGLE_ROOM, SEPARATE_ROOMS, SEPARATE_ROOMS_3, CONNECTING_ROOMS] = [
    'SINGLE_ROOM',
    'SEPARATE_ROOMS',
    'SEPARATE_ROOMS_3',
    'CONNECTING_ROOMS',
  ];
  const matchSingleConfiguration = roomType === SINGLE_ROOM;
  const matchSeparatedConfiguration = roomType === SEPARATE_ROOMS;
  const matchSeparated3Configuration = roomType === SEPARATE_ROOMS_3;
  const matchCommunicatingConfiguration = roomType === CONNECTING_ROOMS;

  if (matchSingleConfiguration) {
    return accommodations.length === 1 && accommodations[0].roomType === SINGLE_ROOM;
  }

  if (matchSeparatedConfiguration) {
    const separateConfigurations =
      (accommodations.length === 2 &&
        accommodations.every(
          (accommodation) =>
            accommodation.roomType === SEPARATE_ROOMS || accommodation.roomType === SINGLE_ROOM,
        )) || // some combinations of two or more rooms come in one accommodation with ex : C4+C4 but roomtype is SEPARATE_ROOMS
      (accommodations.length === 1 && accommodations[0].roomType === SEPARATE_ROOMS);
    return separateConfigurations;
  }

  if (matchSeparated3Configuration) {
    const separateConfigurations =
      (accommodations.length >= 3 &&
        accommodations.every(
          (accommodation) =>
            accommodation.roomType === SEPARATE_ROOMS ||
            accommodation.roomType === SEPARATE_ROOMS_3 ||
            accommodation.roomType === SINGLE_ROOM,
        )) ||
      (accommodations.length === 1 && accommodations[0].roomType === SEPARATE_ROOMS_3);
    return separateConfigurations;
  }

  if (matchCommunicatingConfiguration) {
    const communicatingConfigurations =
      accommodations.length >= 1 &&
      accommodations.some((accommodation) => accommodation.roomType === CONNECTING_ROOMS);
    return communicatingConfigurations;
  }

  // default case for all other room types ( we never know what to expect )
  return accommodations.some((accommodation: Accommodation) => accommodation.roomType === roomType);
};

const matchComfort = (accommodations: Array<Accommodation>, categoryId: string): boolean => {
  return accommodations.some((accommodation: Accommodation) =>
    get(accommodation, 'categories').find(
      (category: Category) => get(category, 'id') === categoryId,
    ),
  );
};

const matchExtraFeatures = (accommodations: Array<Accommodation>, strengthId: string): boolean => {
  return accommodations.some(
    (accommodation: Accommodation) =>
      get(accommodation, 'view.id', null) === strengthId ||
      get(accommodation, 'strengths', []).find(
        (strength: Strength) => get(strength, 'id') === strengthId,
      ),
  );
};

const matchExtraFeaturesReducedMobility = (accommodations: Array<Accommodation>): boolean => {
  return accommodations.some((accommodation: Accommodation) => accommodation.reducedMobilityAccess);
};

function createConfigurationFilters(
  accommodationsSets: Array<AccommodationsSet>,
  product: Product,
): Array<Filter> {
  const accommodations = pipe([map(path('accommodations')), flatten, uniqBy('roomType')])(
    accommodationsSets,
  );
  const roomTypes = accommodations.map(({ roomType }) => roomType);
  // even we dont have the SEPARATE_ROOMS in the roomTypes, we need to show the filter
  // because two or more with SINGLE_ROOM can be combined to form SEPARATE_ROOMS
  // same for SEPARATE_ROOMS_3
  const roomTypesSR = roomTypes.includes('SEPARATE_ROOMS')
    ? roomTypes
    : [...roomTypes, 'SEPARATE_ROOMS'];
  const completeRoomTypes = roomTypes.includes('SEPARATE_ROOMS_3')
    ? roomTypesSR
    : [...roomTypesSR, 'SEPARATE_ROOMS_3'];
  return completeRoomTypes.map((roomType) => ({
    id: crypto.randomUUID(),
    category: CONFIGURATION_CATEGORY,
    match: matchConfiguration,
    meta: {
      label: tAlt({
        cruiseKeyPath: `filters.configuration.${CABIN_TYPE[roomType]}`,
        product,
        resortKeyPath: `filters.configuration.${ROOM_TYPE[roomType]}`,
      }),
      icon: ICONS[roomType],
    },
    path: 'accommodations',
    selected: false,
    value: roomType,
  }));
}

function createComfortFilters(accommodationsSets: Array<AccommodationsSet>): Array<Filter> {
  const accommodations = pipe([
    map(path('accommodations')),
    flatten,
    compact,
    uniqBy('categoryId'),
  ])(accommodationsSets);
  const comfortSeen = [];
  return accommodations.map((accommodation) => {
    /**
     * in some cases we have differents categories with same comfort type
     * it causes a duplicate filter
     * Concat comfort type & category, as label for teh second one
     */
    const { comfortType, category } = accommodation;
    const label = comfortSeen.includes(comfortType)
      ? `${comfortType} ${category}`
      : comfortType || category;
    comfortSeen.push(accommodation.comfortType);
    return {
      id: crypto.randomUUID(),
      category: COMFORT_CATEGORY,
      match: matchComfort,
      meta: {
        label,
      },
      path: 'accommodations',
      selected: false,
      value: accommodation.categoryId,
    };
  });
}

function createExtraFeaturesFilters(accommodationsSets: Array<AccommodationsSet>): Array<Filter> {
  const accommodations = pipe([map(path('accommodations')), flatten, uniqBy('id')])(
    accommodationsSets,
  );
  const strengths = pipe([map(path('strengths')), flatten, compact, uniqBy('id')])(accommodations);
  const views = pipe([map(path('view')), compact, uniqBy('id')])(accommodations);
  const features = [...strengths, ...views];
  const filters = features.map((feature) => ({
    id: crypto.randomUUID(),
    category: EXTRA_FEATURES_CATEGORY,
    match:
      feature.id === REDUCED_MOBILITY_FILTER_ID
        ? matchExtraFeaturesReducedMobility
        : matchExtraFeatures,
    meta: {
      label: feature.label,
      icon: ICONS[feature.id],
      iconClassName: `icon--${feature.id}`,
      iconInheritColor: true,
    },
    path: 'accommodations',
    selected: false,
    value: feature.id,
  }));

  // Always expose the PMR filter when API returned at least one PMR room
  const hasPmr = accommodations.some((accommodation) => accommodation.reducedMobilityAccess);
  if (hasPmr && !filters.some((filter) => filter.value === REDUCED_MOBILITY_FILTER_ID)) {
    filters.push({
      id: crypto.randomUUID(),
      category: EXTRA_FEATURES_CATEGORY,
      match: matchExtraFeaturesReducedMobility,
      meta: {
        label: t('filters.reducedMobility'),
        icon: ICONS.reduced_mobility_access,
        iconClassName: 'icon--reduced_mobility_access',
        iconInheritColor: true,
      },
      path: 'accommodations',
      selected: false,
      value: REDUCED_MOBILITY_FILTER_ID,
    });
  }

  return filters;
}

function handleReducedMobilityFilter(
  accommodationsSets: any,
  reducedMobilityVisibility: boolean,
  state: any,
) {
  const accommodations = pipe([map(path('accommodations')), flatten, uniqBy('id')])(
    accommodationsSets,
  );
  const isReducedMobility = accommodations.some(
    (accommodation) => accommodation.reducedMobilityAccess,
  );
  const matchedReducedMobility = isReducedMobility
    ? accommodationsSets.filter(
        (filteredAccomodation) =>
          filteredAccomodation.accommodations.filter((acc) => acc.reducedMobilityAccess === true)
            .length > 0,
      ).length
    : 0;
  const newItem = {
    id: crypto.randomUUID(),
    category: EXTRA_FEATURES_CATEGORY,
    match: matchExtraFeaturesReducedMobility,
    matchedOccurrences: matchedReducedMobility,
    meta: {
      label: t('filters.reducedMobility'),
      icon: ICONS.reduced_mobility_access,
      iconClassName: 'icon--reduced_mobility_access',
      iconInheritColor: true,
    },
    path: 'accommodations',
    selected: false,
    value: 'reduced_mobility_access',
  };
  if (!reducedMobilityVisibility) {
    const alreadyPresent = state.some((feature) => feature.value === REDUCED_MOBILITY_FILTER_ID);
    return alreadyPresent ? state : [...state, newItem];
  }

  return state.filter((feature) => feature.value !== REDUCED_MOBILITY_FILTER_ID);
}

function createFilters(
  accommodationsSets: Array<AccommodationsSet>,
  product: Product,
): Array<Filter> {
  return [
    ...createExtraFeaturesFilters(accommodationsSets),
    ...createConfigurationFilters(accommodationsSets, product),
    ...createComfortFilters(accommodationsSets),
  ];
}

function unselectCategory(filters: Array<Filter>): (arg0: string) => Array<Filter> {
  return (category) => {
    return filters.map((filter) => ({
      ...filter,
      selected: filter.category === category ? false : filter.selected,
    }));
  };
}

type UpdateFiltersArgs = {
  accommodationsSets: Array<AccommodationsSet>;
  filterValues: Array<Filter>;
  visibility: boolean;
};
export const updateFilters =
  ({ accommodationsSets, filterValues, visibility }: UpdateFiltersArgs, dispatch: ReduxDispatch) =>
  (_name: string, _opts: Array<FilterOptionProps>, updatedValue?: FilterOptionProps['value']) => {
    if (filterValues && updatedValue) {
      const f = filterValues.find((v) => v.value === updatedValue);

      if (f) {
        dispatch(toggleAdditionalAccommodationFilter(f));
      }
      return;
    }

    // PMR switch: reveal/hide PMR rooms without selecting the exclusive filter
    const nextVisibility = !visibility;
    dispatch(togglePMRChecbox(nextVisibility, filterValues));

    const hasPmrFilter = Boolean(
      filterValues?.some((filter) => filter.value === REDUCED_MOBILITY_FILTER_ID),
    );
    const hasPmrRooms = accommodationsSets.some((set) =>
      set.accommodations?.some(({ reducedMobilityAccess }) => reducedMobilityAccess === true),
    );

    if (nextVisibility) {
      // Switch ON: always expose the PMR filter option (even at 0 → greyed)
      if (!hasPmrFilter) {
        dispatch(
          toggleAdditionalAccommodationReducedMobilityFilter(accommodationsSets, visibility),
        );
      }
      return;
    }

    // Switch OFF: remove the (0) greyed option only when there is no PMR inventory.
    // If API has PMR rooms, keep the filter available (Feedback A).
    if (hasPmrFilter && !hasPmrRooms) {
      dispatch(toggleAdditionalAccommodationReducedMobilityFilter(accommodationsSets, visibility));
    }
  };
export {
  COMFORT_CATEGORY,
  EXTRA_FEATURES_CATEGORY,
  CONFIGURATION_CATEGORY,
  REDUCED_MOBILITY_CATEGORY,
  REDUCED_MOBILITY_FILTER_ID,
  matchConfiguration,
  matchComfort,
  matchExtraFeatures,
  createFilters,
  createConfigurationFilters,
  createComfortFilters,
  createExtraFeaturesFilters,
  unselectCategory,
  handleReducedMobilityFilter,
  matchExtraFeaturesReducedMobility,
};
